import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { GuidedTourLayout, GuidedTourStep } from '../components/guidedTour/types';
import {
  anchorTourTooltip,
  centerTourTooltip,
  clearTourHighlights,
  isElementInViewport,
  scrollTourTargetIntoView,
  waitForNextFrame,
  waitForScrollEnd,
} from '../utils/guidedTour';

export interface UseGuidedTourOptions {
  isOpen: boolean;
  steps: GuidedTourStep[];
  onClose: () => void;
  layout?: GuidedTourLayout;
  resetKey?: string | number;
  extraHighlightClasses?: string[];
  skipHighlightOnLastStep?: boolean;
  onStepEnter?: (step: GuidedTourStep, index: number) => void | Promise<void>;
}

export function useGuidedTour({
  isOpen,
  steps,
  onClose,
  layout = 'center',
  resetKey,
  extraHighlightClasses = [],
  skipHighlightOnLastStep = false,
  onStepEnter,
}: UseGuidedTourOptions) {
  const [currentStep, setCurrentStep] = useState(0);
  const [tooltipPosition, setTooltipPosition] = useState(() => ({
    x: typeof window !== 'undefined' ? window.innerWidth / 2 : 0,
    y: typeof window !== 'undefined' ? window.innerHeight / 2 : 0,
  }));
  const [currentPosition, setCurrentPosition] = useState<'top' | 'bottom' | 'left' | 'right'>('top');
  const [isStepTransitioning, setIsStepTransitioning] = useState(false);
  const highlightedRef = useRef<HTMLElement | null>(null);
  const mountGenerationRef = useRef(0);

  const stepCount = steps.length;
  const safeStepIndex = stepCount === 0 ? 0 : Math.min(Math.max(0, currentStep), stepCount - 1);

  const removeHighlight = useCallback(() => {
    if (highlightedRef.current) {
      highlightedRef.current.classList.remove('onboarding-highlight', ...extraHighlightClasses);
      highlightedRef.current = null;
    }
    clearTourHighlights(extraHighlightClasses);
  }, [extraHighlightClasses]);

  const applyTooltipPosition = useCallback(
    (element: HTMLElement, step: GuidedTourStep, index: number) => {
      if (layout === 'anchor') {
        const isLast = index === stepCount - 1;
        const anchored = anchorTourTooltip(element, step, isLast);
        setTooltipPosition({ x: anchored.x, y: anchored.y });
        setCurrentPosition(anchored.position);
        return;
      }

      const centered = centerTourTooltip('.guided-tour-tooltip');
      setTooltipPosition(centered);
      setCurrentPosition('top');
    },
    [layout, stepCount]
  );

  const mountStepTarget = useCallback(
    async (element: HTMLElement, step: GuidedTourStep, index: number, generation: number) => {
      setIsStepTransitioning(true);
      scrollTourTargetIntoView(element, step.scrollIntoViewBlock ?? 'center');
      if (isElementInViewport(element)) {
        await waitForNextFrame(1);
      } else {
        await waitForScrollEnd();
      }
      if (mountGenerationRef.current !== generation) return;

      clearTourHighlights(extraHighlightClasses);
      const isLast = index === stepCount - 1;
      if (!(skipHighlightOnLastStep && isLast)) {
        element.classList.add('onboarding-highlight', ...extraHighlightClasses);
      }
      highlightedRef.current = element;

      await waitForNextFrame(2);
      if (mountGenerationRef.current !== generation) return;
      applyTooltipPosition(element, step, index);

      await new Promise<void>((resolve) => {
        window.setTimeout(resolve, 60);
      });
      if (mountGenerationRef.current !== generation) return;
      applyTooltipPosition(element, step, index);
      setIsStepTransitioning(false);
    },
    [applyTooltipPosition, extraHighlightClasses, skipHighlightOnLastStep, stepCount]
  );

  useLayoutEffect(() => {
    if (isOpen) setCurrentStep(0);
  }, [isOpen, resetKey]);

  useEffect(() => {
    if (!isOpen || stepCount === 0) return undefined;

    const generation = mountGenerationRef.current + 1;
    mountGenerationRef.current = generation;

    let cancelled = false;
    const step = steps[safeStepIndex];

    const run = async () => {
      if (onStepEnter) {
        await onStepEnter(step, safeStepIndex);
      }
      if (cancelled || mountGenerationRef.current !== generation) return;

      for (let attempt = 0; attempt < 5; attempt += 1) {
        const element = document.getElementById(step.id);
        if (element) {
          await mountStepTarget(element, step, safeStepIndex, generation);
          return;
        }
        await new Promise<void>((resolve) => {
          window.setTimeout(resolve, 200);
        });
        if (cancelled || mountGenerationRef.current !== generation) return;
      }
      setIsStepTransitioning(false);
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [isOpen, safeStepIndex, stepCount, steps, onStepEnter, mountStepTarget]);

  useEffect(() => {
    if (!isOpen || stepCount === 0) return undefined;

    const handleResize = () => {
      const element = highlightedRef.current;
      if (!element) return;
      const step = steps[safeStepIndex];
      window.setTimeout(() => {
        scrollTourTargetIntoView(element, step.scrollIntoViewBlock ?? 'center');
        applyTooltipPosition(element, step, safeStepIndex);
      }, 150);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isOpen, safeStepIndex, stepCount, steps, applyTooltipPosition]);

  useEffect(() => {
    if (!isOpen) {
      mountGenerationRef.current += 1;
      setIsStepTransitioning(false);
      removeHighlight();
    }
  }, [isOpen, removeHighlight]);

  const handleNext = useCallback(() => {
    if (safeStepIndex < stepCount - 1) {
      setCurrentStep(safeStepIndex + 1);
    } else {
      removeHighlight();
      onClose();
    }
  }, [onClose, removeHighlight, safeStepIndex, stepCount]);

  const handlePrev = useCallback(() => {
    setCurrentStep((value) => Math.max(0, value - 1));
  }, []);

  const handleSkip = useCallback(() => {
    removeHighlight();
    onClose();
  }, [onClose, removeHighlight]);

  const currentStepData = steps[safeStepIndex];

  return {
    safeStepIndex,
    stepCount,
    currentStepData,
    tooltipPosition,
    currentPosition,
    isStepTransitioning,
    handleNext,
    handlePrev,
    handleSkip,
  };
}
